/**
 * AddTenantModal - Pick from existing tenants to assign to this property.
 * New tenants must be created via the New Tenancy Wizard only.
 */

import React, { useState, useEffect } from 'react';
import { tenantService } from '../../services/api';

interface AddTenantModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    propertyId?: string;
}

export default function AddTenantModal({ isOpen, onClose, onSuccess, propertyId }: AddTenantModalProps) {
    const [tenants, setTenants] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (isOpen) {
            setSelectedId(null);
            setSearch('');
            setError(null);
            fetchTenants();
        }
    }, [isOpen]);

    const fetchTenants = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await tenantService.getTenants();
            const data = res.data?.data || res.data || [];
            const allTenants = Array.isArray(data) ? data : [];
            // Filter out tenants already assigned to this property
            const available = allTenants.filter((t: any) =>
                !t.propertyId || (propertyId && String(t.propertyId) !== String(propertyId))
            );
            setTenants(available);
            if (available.length === 0) {
                setError('No unassigned tenants available. Create a new tenant via the New Tenancy Wizard.');
            }
        } catch (err: any) {
            console.error('Failed to fetch tenants:', err);
            setError('Failed to load tenants. Please try again.');
            setTenants([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!selectedId || !propertyId) return;
        setSubmitting(true);
        setError(null);
        try {
            await tenantService.updateTenant(selectedId, { propertyId });
            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Assign tenant error:', err);
            setError(err.response?.data?.message || 'Failed to assign tenant to property');
        } finally {
            setSubmitting(false);
        }
    };

    const filtered = tenants.filter((t: any) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (t.name || '').toLowerCase().includes(q) ||
            (t.email || '').toLowerCase().includes(q) ||
            (t.phone || '').includes(q)
        );
    });

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

                <div className="relative inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle bg-white shadow-xl rounded-2xl transform transition-all">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-bold text-gray-900">
                            <i className="fas fa-user-plus text-emerald-500 mr-2"></i>
                            Assign Existing Tenant
                        </h3>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <i className="fas fa-times text-xl"></i>
                        </button>
                    </div>

                    <p className="text-sm text-gray-500 mb-4">
                        Select a tenant to assign to this property. To create a new tenant, use the{' '}
                        <strong>New Tenancy Wizard</strong> from the Tenants page.
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            <i className="fas fa-exclamation-circle mr-2"></i>
                            {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <i className="fas fa-spinner fa-spin text-emerald-500 text-2xl"></i>
                            <span className="ml-3 text-gray-500">Loading tenants...</span>
                        </div>
                    ) : (
                        <>
                            {tenants.length > 0 && (
                                <div className="mb-4">
                                    <input
                                        type="text"
                                        placeholder="Search by name, email, or phone..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    />
                                </div>
                            )}

                            <div className="max-h-80 overflow-y-auto space-y-2">
                                {filtered.map((t: any) => (
                                    <div
                                        key={t.id}
                                        onClick={() => setSelectedId(t.id)}
                                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                            selectedId === t.id
                                                ? 'border-emerald-500 bg-emerald-50'
                                                : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-gray-900">{t.name || 'Unnamed'}</div>
                                                <div className="text-sm text-gray-500">{t.email || ''}{t.phone ? ` — ${t.phone}` : ''}</div>
                                            </div>
                                            {selectedId === t.id && (
                                                <i className="fas fa-check-circle text-emerald-500 text-xl"></i>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {!loading && filtered.length === 0 && (
                                    <div className="text-center py-8 text-gray-400">
                                        {search ? 'No matching tenants found.' : 'No unassigned tenants available.'}
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={!selectedId || submitting}
                                    onClick={handleSubmit}
                                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center"
                                >
                                    {submitting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin mr-2"></i>
                                            Assigning...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-link mr-2"></i>
                                            Assign to Property
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
